import { useState } from 'react';
import { C, CLIST, CCOL } from '../constants';
import { TASK_PRIORITY } from '../constants';
import { AppShell, TopBar, BackBtn } from '../components/Atoms';
import { sendPush } from '../push';

const TASK_TYPES = [
  { k: 'general', label: 'General', color: '#C8DFF0' },
  { k: 'labs',    label: 'Labs',    color: '#FF9500'  },
  { k: 'imaging', label: 'Imaging', color: '#64D2FF'  },
];

// Same rules as TasksTab: general = Pending/Done only; labs/imaging = Pending/Done/With Results
const TASK_STATUS = {
  general: ['Pending', 'Done'],
  labs:    ['Pending', 'Done', 'With Results'],
  imaging: ['Pending', 'Done', 'With Results'],
};
const STATUS_COLOR = { 'Pending': '#FF9500', 'Done': '#30D158', 'With Results': '#BF5AF2' };
const STATUS_BG    = { 'Pending': 'rgba(255,149,0,0.12)', 'Done': 'rgba(48,209,88,0.12)', 'With Results': 'rgba(191,90,242,0.12)' };
const STATUS_ICON  = { 'Pending': '○', 'Done': '✓', 'With Results': '◎' };

const UORD = { critical: 0, new: 1, stable: 2, mgh: 3, discharged: 4, mortality: 5 };
const UCOL  = { critical: '#FF4545', new: '#4A9EFF', stable: '#30D158', mgh: '#FF9500', discharged: '#8E8E93', mortality: '#8E8E93' };

export default function AllTasksView({ pts, teams = [], subspecs = [], teamFilter, setTeamFilter, ssFilter, setSsFilter, svcFilter, setSvcFilter, showDone, setShowDone, onClose, onSelectPt, onToggleTask, shiftCode }) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const allTasks = [];
  pts.filter(p => p.urgency !== 'discharged' && p.urgency !== 'mortality').forEach(p => {
    (p.todos || []).forEach(t => {
      allTasks.push({ ...t, _ptId: p.id, _ptName: p.name, _ptRoom: p.room, _ptUrgency: p.urgency, _ptTeam: p.team, _ptSubspec: p.subspec, _ptCode: p.code });
    });
  });

  const PORD = { urgent: 0, soon: 1, routine: 2 };
  allTasks.sort((a, b) => {
    const pp = (PORD[a.priority || 'routine'] ?? 2) - (PORD[b.priority || 'routine'] ?? 2);
    if (pp !== 0) return pp;
    return (UORD[a._ptUrgency] ?? 5) - (UORD[b._ptUrgency] ?? 5);
  });

  // Apply all 3 filters together
  const applyFilters = (tasks, tf, sf, vf) =>
    tasks
      .filter(t => tf === 'all' || t._ptTeam   === tf)
      .filter(t => sf === 'all' || t._ptSubspec === sf)
      .filter(t => vf === 'all' || t._ptCode    === vf);

  const teamFiltered = applyFilters(allTasks, teamFilter, ssFilter, svcFilter);
  const pendingTasks = teamFiltered.filter(t => (t.status || 'Pending') !== 'Done' && !t.done);
  const doneTasks    = teamFiltered.filter(t => (t.status || 'Pending') === 'Done' || t.done);
  const visibleTasks = showDone ? teamFiltered : pendingTasks;

  const urgentCount = pendingTasks.filter(t => (t.priority || 'routine') === 'urgent').length;
  const soonCount   = pendingTasks.filter(t => (t.priority || 'routine') === 'soon').length;

  // Count pending for a pill respecting the OTHER two active filters
  const countFor = (field, value) => {
    const tf = field === 'team'    ? value     : teamFilter;
    const sf = field === 'subspec' ? value     : ssFilter;
    const vf = field === 'svc'     ? value     : svcFilter;
    return applyFilters(allTasks, tf, sf, vf).filter(t => (t.status || 'Pending') !== 'Done' && !t.done).length;
  };

  const cycleStatus = (e, t) => {
    e.stopPropagation();
    const type    = t.type || 'general';
    const options = TASK_STATUS[type] || TASK_STATUS.general;
    const cur     = t.status || (t.done ? 'Done' : 'Pending');
    const curIdx  = options.indexOf(cur);
    const next    = options[(curIdx < 0 ? 0 : curIdx + 1) % options.length];
    onToggleTask(t._ptId, t.id, next);
    if (shiftCode && next !== cur) {
      const pt = pts.find(p => p.id === t._ptId);
      if (pt) {
        const icon = next === 'Done' ? '✅' : next === 'In Progress' ? '🔄' : next === 'With Results' ? '◎' : '⏳';
        sendPush(shiftCode, icon + ' Task Updated', (pt.name||'Patient') + (pt.room ? ' · Rm '+pt.room : '') + ' — ' + (t.text||'task') + ': ' + next, pt);
      }
    }
  };

  const pillBtn = (active, color, label, count, onClick) => (
    <button key={label} onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 7, border: `1px solid ${active ? color : color + '44'}`, background: active ? color + '22' : C.bg2, color: active ? color : C.dim, fontSize: 10, fontWeight: active ? 800 : 600, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
      {label}
      {count > 0 && <span style={{ fontSize: 9, fontWeight: 800, color: active ? color : C.dim2 }}>{count}</span>}
    </button>
  );

  return (
    <AppShell>
      <TopBar
        left={<BackBtn onClick={onClose} />}
        title="All Tasks"
        right={
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            {urgentCount > 0 && <span style={{ fontSize: 10, fontWeight: 800, color: '#FF4545', background: 'rgba(255,69,69,0.15)', border: '1px solid #FF454544', borderRadius: 6, padding: '3px 6px' }}>{urgentCount}!</span>}
            {soonCount   > 0 && <span style={{ fontSize: 10, fontWeight: 800, color: '#FF9500', background: 'rgba(255,149,0,0.15)', border: '1px solid #FF950044', borderRadius: 6, padding: '3px 6px' }}>{soonCount} soon</span>}
            <button
              onClick={() => setShowDone(v => !v)}
              style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, border: '1px solid ' + (showDone ? '#30D158' : C.bd), background: showDone ? 'rgba(48,209,88,0.15)' : C.bg2, color: showDone ? '#30D158' : C.dim, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {showDone ? 'Hide Done' : 'Show Done'} ({doneTasks.length})
            </button>
          </div>
        }
      />

      {/* Collapsible filter bar */}
      <div style={{ flexShrink: 0, borderBottom: `1px solid ${C.bd}` }}>
        {/* Header row — always visible */}
        <button onClick={() => setFiltersOpen(v => !v)}
          style={{ width: '100%', padding: '6px 14px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Filters</span>
          {/* Active pills when collapsed */}
          {!filtersOpen && (
            <div style={{ display: 'flex', gap: 5, flex: 1, overflow: 'hidden', flexWrap: 'nowrap' }}>
              {teamFilter !== 'all' && (() => { const t = teams.find(x => x.id === teamFilter); return t ? <span style={{ fontSize: 9, fontWeight: 700, color: t.color, background: t.color + '22', border: '1px solid ' + t.color + '44', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>{t.name}</span> : null; })()}
              {ssFilter   !== 'all' && (() => { const s = subspecs.find(x => x.id === ssFilter);   return s ? <span style={{ fontSize: 9, fontWeight: 700, color: s.color, background: s.color + '22', border: '1px solid ' + s.color + '44', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>{s.name}</span> : null; })()}
              {svcFilter  !== 'all' && <span style={{ fontSize: 9, fontWeight: 700, color: CCOL[svcFilter] || '#AAA', background: (CCOL[svcFilter] || '#AAA') + '22', border: '1px solid ' + (CCOL[svcFilter] || '#AAA') + '44', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap' }}>{svcFilter}</span>}
              {teamFilter === 'all' && ssFilter === 'all' && svcFilter === 'all' && <span style={{ fontSize: 9, color: C.dim2 }}>All teams · All subspecs · All services</span>}
            </div>
          )}
          <span style={{ fontSize: 10, color: C.dim2, marginLeft: 'auto', flexShrink: 0, transition: 'transform 0.2s', display: 'inline-block', transform: filtersOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
        </button>
        {/* Expanded rows */}
        {filtersOpen && (
          <>
            {teams.length > 0 && (
              <div style={{ display: 'flex', gap: 5, padding: '0 14px 5px', overflowX: 'auto' }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, alignSelf: 'center', paddingRight: 2 }}>Team</span>
                {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...teams].map(tm =>
                  pillBtn(teamFilter === tm.id, tm.color, tm.name, countFor('team', tm.id), () => setTeamFilter(tm.id))
                )}
              </div>
            )}
            {subspecs.length > 0 && (
              <div style={{ display: 'flex', gap: 5, padding: '0 14px 5px', overflowX: 'auto' }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, alignSelf: 'center', paddingRight: 2 }}>Subspec</span>
                {[{ id: 'all', name: 'All', color: '#C8DFF0' }, ...subspecs].map(s =>
                  pillBtn(ssFilter === s.id, s.color, s.name, countFor('subspec', s.id), () => setSsFilter(s.id))
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 5, padding: '0 14px 8px', overflowX: 'auto' }}>
              <span style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, alignSelf: 'center', paddingRight: 2 }}>Service</span>
              {[{ id: 'all', label: 'All', color: '#C8DFF0' }, ...CLIST.map(c => ({ id: c, label: c, color: CCOL[c] || '#AAA' }))].map(opt =>
                pillBtn(svcFilter === opt.id, opt.color, opt.label || opt.name, countFor('svc', opt.id), () => setSvcFilter(opt.id))
              )}
            </div>
          </>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px 40px' }}>
        {pendingTasks.length === 0 && !showDone && (
          <div style={{ textAlign: 'center', color: C.dim2, padding: '60px 0', fontSize: 14 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
            {teamFilter === 'all' ? 'All tasks complete' : 'No pending tasks for this team'}
          </div>
        )}

        {visibleTasks.map(t => {
          const type    = t.type || 'general';
          const tc      = TASK_TYPES.find(tp => tp.k === type)?.color || '#C8DFF0';
          const status  = t.status || (t.done ? 'Done' : 'Pending');
          const isDone  = status === 'Done';
          const pr      = TASK_PRIORITY.find(p => p.k === (t.priority || 'routine'));
          const uc      = UCOL[t._ptUrgency] || '#AAA';
          const sc      = STATUS_COLOR[status] || C.dim2;
          const sb      = STATUS_BG[status]    || 'transparent';
          const si      = STATUS_ICON[status]  || '○';
          const statusLabel = status === 'With Results' ? 'Results' : status;
          const options = TASK_STATUS[type] || TASK_STATUS.general;
          const tm      = teams.find(x => x.id === t._ptTeam);

          return (
            <div key={t.id}
              style={{ background: isDone ? C.bg2 : C.bg3, border: '1px solid ' + C.bd, borderLeft: '3px solid ' + (isDone ? '#333' : (pr?.color || C.bd)), borderRadius: 10, padding: '10px 12px', marginBottom: 7, opacity: isDone ? 0.55 : 1, transition: 'opacity 0.2s' }}>

              {/* Row 1: badges + task text */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8 }}>
                {pr && pr.k !== 'routine' && !isDone && (
                  <span style={{ fontSize: 9, fontWeight: 900, color: pr.color, background: pr.bg, border: '1px solid ' + pr.color + '44', borderRadius: 5, padding: '2px 5px', flexShrink: 0, marginTop: 2 }}>
                    {pr.label.toUpperCase()}
                  </span>
                )}
                {type !== 'general' && (
                  <span style={{ fontSize: 9, fontWeight: 800, color: tc, background: tc + '22', border: '1px solid ' + tc + '44', borderRadius: 4, padding: '2px 5px', flexShrink: 0, marginTop: 2 }}>
                    {type.toUpperCase()}
                  </span>
                )}
                <span onClick={() => onSelectPt(t._ptId)}
                  style={{ flex: 1, fontSize: 14, fontWeight: 600, color: isDone ? C.dim : C.tx, textDecoration: isDone ? 'line-through' : 'none', cursor: 'pointer', lineHeight: 1.3 }}>
                  {t.text || 'Untitled task'}
                </span>
              </div>

              {/* Row 2: room + name + team badge + status toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span onClick={() => onSelectPt(t._ptId)} style={{ fontSize: 10, color: uc, fontWeight: 700, cursor: 'pointer' }}>
                  Rm {t._ptRoom || '?'}
                </span>
                <span onClick={() => onSelectPt(t._ptId)} style={{ fontSize: 12, color: C.dim, fontWeight: 600, flex: 1, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t._ptName || 'Unknown'}
                </span>
                {tm && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: tm.color, background: tm.color + '22', border: `1px solid ${tm.color}44`, borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>
                    {tm.name}
                  </span>
                )}
                <button onClick={e => cycleStatus(e, t)}
                  title={`Tap to cycle: ${options.join(' → ')}`}
                  style={{ fontSize: 11, fontWeight: 700, color: sc, background: sb, border: '1px solid ' + sc + '55', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                  {si} {statusLabel}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
